'use client';
import {
  BlueGradientBackground,
  ClientOnly,
  cx,
} from '@otter-ai/den-components';
import { useDesktop } from '@otter-ai/desktop';
import { useAtomValue } from 'jotai';
import { usePathname } from 'next/navigation';
import { SIGNIN_HEADER_HEIGHT } from '@/constants/constant';
import { PATH_URL } from '@/constants/path-constants';
import { referralSignupEnabledAtom } from '@/features/auth/atoms/referralSignupEnabledAtom';
import { sharingTokenFlowAtom } from '@/features/auth/atoms/sharingTokenFlowAtom';
import { LoginHeader } from '@/features/auth/components/LoginHeader';
import { SharingTokenFlowType } from '@/shared/types/auth';

type Props = {
  children: React.ReactNode;
};

// This layout is used to provide a context to the auth pages
//  particularly, storing email, password, and create account state in sessionStorage
export default function AuthLayout({ children }: Props) {
  const pathName = usePathname();
  const { isDesktop } = useDesktop();
  const centeredRoutes = [
    PATH_URL.SIGNUP,
    PATH_URL.SIGNIN,
    PATH_URL.PASSWORD,
    PATH_URL.SETUP_PROFILE,
    PATH_URL.TWO_FACTOR,
  ];

  const blueGradientBackgroundRoutes = [PATH_URL.SIGNUP, PATH_URL.SIGNIN];

  const isCentered = centeredRoutes.some(
    (route) => pathName === `/${route}` || pathName.startsWith(`/${route}/`),
  );

  const clickToSignupEnabled =
    useAtomValue(sharingTokenFlowAtom) === SharingTokenFlowType.CLICK_TO_SIGNUP;

  const referralSignupEnabled = useAtomValue(referralSignupEnabledAtom);

  const isBlueGradientBackground =
    (clickToSignupEnabled &&
      blueGradientBackgroundRoutes.some((route) => pathName === `/${route}`)) ||
    referralSignupEnabled;

  const content = (
    <>
      <LoginHeader
        showShadow={!clickToSignupEnabled && !referralSignupEnabled}
        logoCenterOnMobile={referralSignupEnabled}
      />
      <div
        className={cx(
          'flex items-start p-5 md:p-0 justify-center',
          isCentered && 'md:items-center',
        )}
        style={{ minHeight: `calc(100vh - ${SIGNIN_HEADER_HEIGHT}px)` }}
      >
        {children}
      </div>
    </>
  );

  return (
    <ClientOnly>
      {isBlueGradientBackground ? (
        <BlueGradientBackground
          size="fit"
          className="h-screen w-screen overflow-auto"
        >
          {content}
        </BlueGradientBackground>
      ) : (
        <div
          className={cx(
            'h-screen w-screen overflow-auto',
            isDesktop && 'bg-default',
          )}
        >
          {content}
        </div>
      )}
    </ClientOnly>
  );
}
